'use client';
import { useEffect } from 'react';
import { Box, Typography, Button, Container, Card, CardContent, Grid, Paper, Chip, LinearProgress } from '@mui/material';
import { Warning, TrendingUp, Schedule, Group, Assessment, Security, Speed, CrisisAlert, Psychology, WorkOff } from '@mui/icons-material';
import TopNavigation from '@/components/TopNavigation';
import Footer from '@/components/Footer';

export default function Challenges() {
  useEffect(() => {
    document.title = 'CareAscendium - Challenges';
  }, []);
  const challenges = [
    {
      icon: Warning,
      title: "Regulatory Complexity",
      description: "Navigating complex CMS regulations, state-specific requirements, and frequent policy changes that require constant adaptation and compliance monitoring.",
      impact: "High risk of citations and penalties",
      severity: 95,
      category: "Compliance"
    },
    {
      icon: TrendingUp,
      title: "Staffing Shortages",
      description: "Critical shortage of qualified healthcare professionals, leading to increased workload, burnout, and compromised care quality.",
      impact: "Reduced care quality and staff retention issues",
      severity: 90,
      category: "Human Resources"
    },
    {
      icon: Schedule,
      title: "Documentation Burden",
      description: "Excessive paperwork and redundant documentation requirements that consume valuable staff time away from direct resident care.",
      impact: "10+ hours per month lost to paperwork",
      severity: 85,
      category: "Efficiency"
    },
    {
      icon: Group,
      title: "Communication Gaps",
      description: "Breakdowns in communication between shifts, departments, and care teams leading to missed information and care coordination issues.",
      impact: "Medication errors and care plan inconsistencies",
      severity: 80,
      category: "Communication"
    },
    {
      icon: Assessment,
      title: "Quality Assurance",
      description: "Difficulty maintaining consistent quality standards across all aspects of care delivery and monitoring performance metrics.",
      impact: "Lower CMS star ratings and resident satisfaction",
      severity: 85,
      category: "Quality"
    },
    {
      icon: Security,
      title: "Data Security",
      description: "Protecting sensitive resident information while ensuring accessibility for authorized staff and maintaining HIPAA compliance.",
      impact: "Risk of data breaches and compliance violations",
      severity: 90,
      category: "Security"
    },
    {
      icon: Speed,
      title: "Operational Inefficiency",
      description: "Inefficient workflows, manual processes, and lack of automation leading to wasted time and resources.",
      impact: "Increased operational costs and staff frustration",
      severity: 75,
      category: "Operations"
    }
  ];

  const categories = [...new Set(challenges.map(c => c.category))];
  const averageSeverity = Math.round(challenges.reduce((sum, c) => sum + c.severity, 0) / challenges.length);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      {/* Top Navigation Bar */}
      <TopNavigation />

      {/* Hero Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #dc3545 0%, #ff6b35 50%, #ff8c42 100%)',
        py: 12, 
        px: 4,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)',
          backgroundSize: '50px 50px'
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
            <CrisisAlert sx={{ fontSize: 64, color: 'white', mr: 3 }} />
            <Typography 
              variant="h1" 
              sx={{ 
                color: 'white', 
                mb: 4,
                fontWeight: 800,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              Understanding SNF Challenges
            </Typography>
          </Box>
          <Typography 
            variant="h5" 
            sx={{ 
              color: 'rgba(255,255,255,0.9)',
              maxWidth: '900px',
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.2rem', md: '1.5rem' }
            }}
          >
            Skilled Nursing Facilities face complex, interrelated challenges that impact every aspect of care delivery and facility operations
          </Typography>
        </Container>
      </Box>

      {/* Challenge Overview Stats */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper 
              elevation={6}
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(220,53,69,0.1)'
              }}
            >
              <CrisisAlert sx={{ fontSize: 48, color: '#dc3545', mb: 2 }} />
              <Typography variant="h3" sx={{ color: '#dc3545', fontWeight: 700, mb: 1 }}>
                {challenges.length}
              </Typography>
              <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>
                Major Challenges
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper 
              elevation={6}
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(220,53,69,0.1)'
              }}
            >
              <Psychology sx={{ fontSize: 48, color: '#ff6b35', mb: 2 }} />
              <Typography variant="h3" sx={{ color: '#ff6b35', fontWeight: 700, mb: 1 }}>
                {averageSeverity}%
              </Typography>
              <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>
                Average Severity
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper 
              elevation={6}
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(220,53,69,0.1)'
              }}
            >
              <WorkOff sx={{ fontSize: 48, color: '#ff8c42', mb: 2 }} />
              <Typography variant="h3" sx={{ color: '#ff8c42', fontWeight: 700, mb: 1 }}>
                {categories.length}
              </Typography>
              <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>
                Challenge Categories
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper 
              elevation={6}
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(220,53,69,0.1)'
              }}
            >
              <Warning sx={{ fontSize: 48, color: '#dc3545', mb: 2 }} />
              <Typography variant="h3" sx={{ color: '#dc3545', fontWeight: 700, mb: 1 }}>
                High
              </Typography>
              <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>
                Risk Level
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Challenge Ecosystem Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" sx={{ color: '#dc3545', mb: 4, fontWeight: 700 }}>
            The Challenge Ecosystem
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333', fontSize: '1.1rem' }}>
            These seven challenges are interconnected, creating a complex web that affects facility performance, resident outcomes, and staff satisfaction
          </Typography>
        </Box>

        {/* Circular Layout */}
        <Box sx={{ 
          position: 'relative',
          width: '100%',
          height: '600px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 6
        }}>
          {/* Center */}
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '140px',
            height: '140px',
            borderRadius: '50%',
            background: 'linear-gradient(45deg, #dc3545, #ff6b35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
            boxShadow: '0 8px 24px rgba(220,53,69,0.3)'
          }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, textAlign: 'center', fontSize: '0.9rem' }}>
              SNF<br />Challenges
            </Typography>
          </Box>

          {/* Challenge Cards in Circle */}
          {challenges.map((challenge, index) => {
            const angle = (index * 360) / challenges.length;
            const radius = 220;
            const x = Math.cos((angle - 90) * Math.PI / 180) * radius;
            const y = Math.sin((angle - 90) * Math.PI / 180) * radius;
            
            return (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                  width: '200px',
                  zIndex: 1
                }}
              >
                <Paper 
                  elevation={8}
                  sx={{ 
                    textAlign: 'center',
                    p: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                    border: '1px solid rgba(220,53,69,0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: '0 12px 32px rgba(220,53,69,0.2)',
                      zIndex: 3
                    }
                  }}
                >
                  <challenge.icon sx={{ fontSize: 48, color: '#dc3545', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#dc3545', mb: 1, fontWeight: 700, fontSize: '1rem' }}>
                    {challenge.title}
                  </Typography>
                  <Chip 
                    label={challenge.category} 
                    size="small" 
                    sx={{ 
                      bgcolor: '#fff3cd', 
                      color: '#856404',
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }} 
                  />
                </Paper>
              </Box>
            );
          })}
        </Box>
      </Container>

      {/* Detailed Challenges Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)',
        py: 10 
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{ color: '#dc3545', mb: 4, fontWeight: 700 }}>
              Detailed Challenge Analysis
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333', fontSize: '1.1rem' }}>
              Understanding the root causes and impacts of each challenge helps identify targeted solutions
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {challenges.map((challenge, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={index}>
                <Paper 
                  elevation={6}
                  sx={{ 
                    height: '100%',
                    p: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                    border: '1px solid rgba(220,53,69,0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 32px rgba(220,53,69,0.15)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <challenge.icon sx={{ fontSize: 48, color: '#dc3545', mr: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h5" sx={{ color: '#dc3545', fontWeight: 700, mb: 1 }}>
                        {challenge.title}
                      </Typography>
                      <Chip 
                        label={challenge.category} 
                        size="small" 
                        sx={{ 
                          bgcolor: '#fff3cd', 
                          color: '#856404',
                          fontWeight: 600
                        }} 
                      />
                    </Box>
                  </Box>
                  
                  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#333', fontSize: '1.1rem' }}>
                    {challenge.description}
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ color: '#dc3545', fontWeight: 600 }}>
                        Severity Level
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: '#dc3545', fontWeight: 700 }}>
                        {challenge.severity}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={challenge.severity} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: '#ffe0e0',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: '#dc3545'
                        }
                      }} 
                    />
                  </Box>
                  
                  <Box sx={{ 
                    bgcolor: '#fff3cd', 
                    p: 3, 
                    borderRadius: 2,
                    border: '1px solid #ffeaa7'
                  }}>
                    <Typography variant="subtitle1" sx={{ color: '#856404', fontWeight: 700, mb: 1 }}>
                      🚨 Impact:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#856404', lineHeight: 1.6, fontSize: '1rem' }}>
                      {challenge.impact}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Challenge Categories Summary */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" sx={{ color: '#dc3545', mb: 4, fontWeight: 700 }}>
            Challenge Categories
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333', fontSize: '1.1rem' }}>
            Understanding the different types of challenges helps prioritize solutions and resource allocation
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {categories.map((category, index) => {
            const categoryChallenges = challenges.filter(c => c.category === category);
            const avgSeverity = Math.round(categoryChallenges.reduce((sum, c) => sum + c.severity, 0) / categoryChallenges.length);
            
            return (
              <Grid size={{ xs: 12, md: 6 }} key={index}>
                <Paper 
                  elevation={6}
                  sx={{ 
                    p: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                    border: '1px solid rgba(220,53,69,0.1)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(220,53,69,0.15)'
                    }
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#dc3545', mb: 3, fontWeight: 700 }}>
                    {category}
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ color: '#666', fontWeight: 600 }}>
                        Average Severity
                      </Typography>
                      <Typography variant="subtitle1" sx={{ color: '#dc3545', fontWeight: 700 }}>
                        {avgSeverity}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={avgSeverity} 
                      sx={{ 
                        height: 10, 
                        borderRadius: 5,
                        bgcolor: '#ffe0e0',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: '#dc3545'
                        }
                      }} 
                    />
                  </Box>
                  
                  <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
                    <strong>{categoryChallenges.length}</strong> challenges in this category
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {categoryChallenges.map((challenge, idx) => (
                      <Chip 
                        key={idx}
                        label={challenge.title} 
                        size="small" 
                        sx={{ 
                          bgcolor: '#fff3cd', 
                          color: '#856404',
                          fontWeight: 500,
                          fontSize: '0.8rem'
                        }} 
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ 
        py: 12, 
        background: 'linear-gradient(135deg, #dc3545 0%, #ff6b35 100%)',
        color: 'white',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)',
          backgroundSize: '50px 50px'
        }} />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" sx={{ mb: 4, fontWeight: 800, fontSize: { xs: '2rem', md: '3rem' } }}>
            How CareAscendium Solves These Challenges
          </Typography>
          <Typography variant="h5" sx={{ mb: 6, opacity: 0.9, lineHeight: 1.6 }}>
            Our comprehensive platform addresses each challenge with targeted solutions and integrated workflows
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            startIcon={<TrendingUp />}
            sx={{ 
              bgcolor: 'white',
              color: '#dc3545',
              px: 6,
              py: 3,
              fontSize: '1.2rem',
              fontWeight: 700,
              borderRadius: 3,
              textTransform: 'none',
              boxShadow: '0 4px 15px rgba(255,255,255,0.3)',
              '&:hover': {
                bgcolor: '#f5f5f5',
                boxShadow: '0 6px 20px rgba(255,255,255,0.4)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Learn About Our Solutions
          </Button>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
} 